import type { WhoAmIResponse } from '@/types/response'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface AuthState {
  user: WhoAmIResponse | null
  passwordExpired: boolean
  mfaToken: string | null
  pendingToken: { authToken: string; refreshToken: string } | null
  authToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  companyId: number | null
  companyName: string | null
  permissions: string[]
  sessionGeneration: number
  hasAdminSiteAccess: () => boolean

  setUser: (user: WhoAmIResponse | null) => void
  setPasswordExpired: (expired: boolean) => void
  setMFAToken: (token: string | null) => void
  setPendingToken: (accessToken: string | null, refreshToken: string | null) => void
  setAuthToken: (token: string | null) => void
  setAuthTokens: (accessToken: string | null, refreshToken: string | null) => void
  setCompany: (id: number | null, name: string | null) => void
  setCompanyName: (name: string | null) => void
  logout: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      passwordExpired: false,
      mfaToken: null,
      pendingToken: null,
      authToken: null,
      refreshToken: null,
      isAuthenticated: false,
      companyId: null,
      companyName: null,
      permissions: [],
      sessionGeneration: 0,

      hasAdminSiteAccess: () => {
        const state = get()
        return state.user?.is_superuser || state.permissions.includes('common__access_admin_site')
      },

      setUser: (user) =>
        set({
          user: user,
          isAuthenticated: !!user?.id,
          permissions: user?.permissions ?? [],
          companyId: user?.company?.id ?? null,
          companyName: user?.company?.name ?? null,
        }),

      setPasswordExpired: (passwordExpired) => set({ passwordExpired }),

      setMFAToken: (token) =>
        set({
          mfaToken: token,
        }),

      setPendingToken: (accessToken, refreshToken) =>
        set({
          pendingToken: accessToken && refreshToken ? { authToken: accessToken, refreshToken } : null,
        }),

      setAuthToken: (token) =>
        set({
          authToken: token,
          isAuthenticated: !!token,
        }),

      setAuthTokens: (accessToken, refreshToken) =>
        set({
          authToken: accessToken,
          refreshToken,
          isAuthenticated: !!accessToken,
        }),

      setCompany: (id, name) => set({ companyId: id, companyName: name }),

      setCompanyName: (name) => set({ companyName: name }),

      logout: () =>
        set((state) => ({
          user: null,
          passwordExpired: false,
          mfaToken: null,
          pendingToken: null,
          authToken: null,
          refreshToken: null,
          isAuthenticated: false,
          companyId: null,
          companyName: null,
          permissions: [],
          sessionGeneration: state.sessionGeneration + 1,
        })),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        passwordExpired: state.passwordExpired,
        mfaToken: state.mfaToken,
        pendingToken: state.pendingToken,
        authToken: state.authToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        companyId: state.companyId,
        companyName: state.companyName,
        permissions: state.permissions,
      }),
    },
  ),
)

export const getMFAToken = () => useAuthStore.getState().mfaToken
export const getAuthToken = () => useAuthStore.getState().authToken
export const getRefreshToken = () => useAuthStore.getState().refreshToken
